import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  CircularProgress, 
  useTheme, 
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Grid
} from '@mui/material';
import { Person, CheckCircle, Cancel, Schedule } from '@mui/icons-material';
import { getDashboardAnalytics } from '../api/analytics';
import { getUpcomingShifts, EmployeeShift } from '../api/employees';
import { useTranslation } from '../utils/i18n-utils';

interface DashboardData {
  totalSales: number;
  lowStockItems: number;
  topSellingMake: string;
  recentSales: number;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<DashboardData>({
    totalSales: 0,
    lowStockItems: 0,
    topSellingMake: 'N/A',
    recentSales: 0
  });
  const [shifts, setShifts] = useState<{today: EmployeeShift[], tomorrow: EmployeeShift[]}>({
    today: [],
    tomorrow: []
  });
  const [shiftsLoading, setShiftsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [shiftsError, setShiftsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const analyticsData = await getDashboardAnalytics();
        console.log('Dashboard analytics data:', analyticsData);
        setData(analyticsData);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    const fetchShifts = async () => {
      try {
        setShiftsLoading(true);
        const shiftsData = await getUpcomingShifts();
        console.log('Employee shifts data:', shiftsData);
        setShifts(shiftsData);
        setShiftsError(null);
      } catch (err: any) {
        console.error('Error fetching employee shifts:', err);
        setShiftsError('Failed to load employee shifts. Please try again later.');
      } finally {
        setShiftsLoading(false);
      }
    };

    fetchDashboardData();
    fetchShifts();
  }, []);

  // Function to render the status chip for employee shifts
  const renderStatusChip = (status: string) => {
    switch (status) {
      case 'present':
        return <Chip size="small" icon={<CheckCircle />} label={t('employees.status.present')} color="success" />;
      case 'absent':
        return <Chip size="small" icon={<Cancel />} label={t('employees.status.absent')} color="error" />;
      case 'late':
        return <Chip size="small" icon={<Schedule />} label={t('employees.status.late')} color="warning" />;
      default:
        return <Chip size="small" icon={<Schedule />} label={t('employees.status.scheduled')} color="primary" />;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            mb: 0.5,
            color: '#101D68',
            textAlign: isRTL ? 'right' : 'left' 
          }}
        >
          {t('navigation.dashboard')}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#6B7280',
            textAlign: isRTL ? 'right' : 'left' 
          }}
        >
          Welcome back! Here's what's happening today.
        </Typography>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress size={48} sx={{ color: '#101D68' }} />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        </Paper>
      ) : (
        <Box>
          <Grid container spacing={3} direction={isRTL ? 'row-reverse' : 'row'}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 180,
                  background: 'linear-gradient(135deg, #101D68 0%, #2A3A8A 100%)',
                  color: 'white',
                  borderRadius: 3,
                  boxShadow: '0px 8px 24px rgba(16, 29, 104, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 12px 32px rgba(16, 29, 104, 0.3)',
                  },
                }}
              >
                <Typography 
                  component="h2" 
                  variant="body2" 
                  sx={{ 
                    mb: 2,
                    opacity: 0.9,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('dashboard.totalSales')}
                </Typography>
                <Typography 
                  component="p" 
                  variant="h3"
                  sx={{ 
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                  }}
                >
                  د.ل{data.totalSales.toFixed(2)}
                </Typography>
                <Typography 
                  sx={{ 
                    mt: 'auto',
                    opacity: 0.8,
                    fontSize: '0.875rem',
                  }}
                >
                  {t('dashboard.thisMonth')}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 180,
                  background: data.lowStockItems > 0 
                    ? 'linear-gradient(135deg, #b22222 0%, #d32f2f 100%)'
                    : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  color: 'white',
                  borderRadius: 3,
                  boxShadow: data.lowStockItems > 0
                    ? '0px 8px 24px rgba(178, 34, 34, 0.2)'
                    : '0px 8px 24px rgba(16, 185, 129, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: data.lowStockItems > 0
                      ? '0px 12px 32px rgba(178, 34, 34, 0.3)'
                      : '0px 12px 32px rgba(16, 185, 129, 0.3)',
                  },
                }}
              >
                <Typography 
                  component="h2" 
                  variant="body2" 
                  sx={{ 
                    mb: 2,
                    opacity: 0.9,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('dashboard.lowStockItems')}
                </Typography>
                <Typography 
                  component="p" 
                  variant="h3"
                  sx={{ 
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                  }}
                >
                  {data.lowStockItems}
                </Typography>
                <Typography 
                  sx={{ 
                    mt: 'auto',
                    opacity: 0.8,
                    fontSize: '0.875rem',
                  }}
                >
                  {t('dashboard.belowMinimum')}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 180,
                  background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  color: 'white',
                  borderRadius: 3,
                  boxShadow: '0px 8px 24px rgba(99, 102, 241, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 12px 32px rgba(99, 102, 241, 0.3)',
                  },
                }}
              >
                <Typography 
                  component="h2" 
                  variant="body2" 
                  sx={{ 
                    mb: 2,
                    opacity: 0.9,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('dashboard.topSellingMake')}
                </Typography>
                <Typography 
                  component="p" 
                  variant="h3"
                  sx={{ 
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                  }}
                >
                  {data.topSellingMake}
                </Typography>
                <Typography 
                  sx={{ 
                    mt: 'auto',
                    opacity: 0.8,
                    fontSize: '0.875rem',
                  }}
                >
                  {t('dashboard.basedOnSales')}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 180,
                  background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                  color: 'white',
                  borderRadius: 3,
                  boxShadow: '0px 8px 24px rgba(245, 158, 11, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0px 12px 32px rgba(245, 158, 11, 0.3)',
                  },
                }}
              >
                <Typography 
                  component="h2" 
                  variant="body2" 
                  sx={{ 
                    mb: 2,
                    opacity: 0.9,
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('dashboard.recentSales')}
                </Typography>
                <Typography 
                  component="p" 
                  variant="h3"
                  sx={{ 
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                  }}
                >
                  {data.recentSales}
                </Typography>
                <Typography 
                  sx={{ 
                    mt: 'auto',
                    opacity: 0.8,
                    fontSize: '0.875rem',
                  }}
                >
                  {t('dashboard.todayTransactions')}
                </Typography>
              </Paper>
            </Grid>
            
            {/* Employee Shifts Section */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
                }}
              >
                <Typography 
                  component="h2" 
                  variant="h6" 
                  sx={{ 
                    mb: 3,
                    fontWeight: 600,
                    color: '#101D68',
                  }}
                >
                  {t('dashboard.todayShifts')}
                </Typography>
                
                {shiftsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress size={32} sx={{ color: '#101D68' }} />
                  </Box>
                ) : shiftsError ? (
                  <Typography color="error" sx={{ py: 2 }}>
                    {shiftsError}
                  </Typography>
                ) : shifts.today.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    {t('dashboard.noShiftsToday')}
                  </Typography>
                ) : (
                  <List sx={{ p: 0 }}>
                    {shifts.today.map((shift, index) => (
                      <ListItem 
                        key={shift.id} 
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          px: 2,
                          py: 1.5,
                          backgroundColor: index % 2 === 0 ? '#F9FAFB' : 'transparent',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(16, 29, 104, 0.05)',
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar 
                            sx={{ 
                              bgcolor: '#101D68',
                              width: 40,
                              height: 40,
                            }}
                          >
                            <Person />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                              {shift.employee?.name}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                              {renderStatusChip(shift.status)}
                              {shift.startTime && (
                                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                                  {shift.startTime} - {shift.endTime || 'TBD'}
                                </Typography>
                              )}
                              {shift.lateMinutes && shift.lateMinutes > 0 && (
                                <Typography variant="body2" sx={{ color: '#b22222' }}>
                                  {t('employees.lateMinutes', { minutes: shift.lateMinutes })}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
                }}
              >
                <Typography 
                  component="h2" 
                  variant="h6" 
                  sx={{ 
                    mb: 3,
                    fontWeight: 600,
                    color: '#101D68',
                  }}
                >
                  {t('dashboard.tomorrowShifts')}
                </Typography>
                
                {shiftsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress size={32} sx={{ color: '#101D68' }} />
                  </Box>
                ) : shiftsError ? (
                  <Typography color="error" sx={{ py: 2 }}>
                    {shiftsError}
                  </Typography>
                ) : shifts.tomorrow.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    {t('dashboard.noShiftsTomorrow')}
                  </Typography>
                ) : (
                  <List sx={{ p: 0 }}>
                    {shifts.tomorrow.map((shift, index) => (
                      <ListItem 
                        key={shift.id} 
                        sx={{
                          borderRadius: 2,
                          mb: 1,
                          px: 2,
                          py: 1.5,
                          backgroundColor: index % 2 === 0 ? '#F9FAFB' : 'transparent',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: 'rgba(16, 29, 104, 0.05)',
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar 
                            sx={{ 
                              bgcolor: '#101D68',
                              width: 40,
                              height: 40,
                            }}
                          >
                            <Person />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                              {shift.employee?.name}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                              {renderStatusChip(shift.status)}
                              {shift.startTime && (
                                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                                  {shift.startTime} - {shift.endTime || 'TBD'}
                                </Typography>
                              )}
                              {shift.lateMinutes && shift.lateMinutes > 0 && (
                                <Typography variant="body2" sx={{ color: '#b22222' }}>
                                  {t('employees.lateMinutes', { minutes: shift.lateMinutes })}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard; 